import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Table, Button, Space, Modal, Form, Input, Select, message, Spin, Transfer, Tag, Switch, Card, List, DatePicker, Popconfirm } from 'antd'
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  AppstoreOutlined, 
  IdcardOutlined, 
  CheckCircleOutlined,
  StopOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useConfig } from '../../contexts/ConfigContext'
import { useSecureRequest } from '../../hooks/useSecureRequest'
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus'
import CompanyElectronicInvoicingModal from './CompanyElectronicInvoicingModal'

function CompaniesView() {
  const { config } = useConfig()
  const { securePost } = useSecureRequest()
  const [companies, setCompanies] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [editingId, setEditingId] = useState<number | null>(null)
  const didFetch = useRef(false)

  // Facturación Electrónica DIAN
  const [feModalVisible, setFeModalVisible] = useState(false)
  const [selectedFeCompany, setSelectedFeCompany] = useState<any>(null)

  const [modulesModalVisible, setModulesModalVisible] = useState(false)
  const [allModules, setAllModules] = useState<any[]>([])
  const [targetKeys, setTargetKeys] = useState<React.Key[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null)
  const [transferLoading, setTransferLoading] = useState(false)

  const [submitLoading, setSubmitLoading] = useState(false)

  // Suscripciones
  const [subModalVisible, setSubModalVisible] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<any>(null)
  const [companySubs, setCompanySubs] = useState<any[]>([])
  const [availablePlans, setAvailablePlans] = useState<any[]>([])
  const [subLoading, setSubLoading] = useState(false)
  const [subFormLoading, setSubFormLoading] = useState(false)
  const [subForm] = Form.useForm()

  // Edición rápida de suscripción
  const [editingSub, setEditingSub] = useState<any>(null)
  const [editSubModalVisible, setEditSubModalVisible] = useState(false)
  const [editSubForm] = Form.useForm()
  const [editSubLoading, setEditSubLoading] = useState(false)

  const fetchCompanies = useCallback(async (isBackground = false) => {
    if (!config.key) return
    if (!isBackground) setLoading(true)
    try {
      const res = await securePost('/company/list', { session: config.session })
      if (res && Array.isArray(res.companyList)) {
        setCompanies(res.companyList)
      } else {
        setCompanies([])
      }
    } catch (err) {
      console.error(err)
      message.error('Error al cargar empresas')
    } finally {
      if (!isBackground) setLoading(false)
    }
  }, [config.key, config.session])

  useEffect(() => {
    if (config.key && !didFetch.current) {
      didFetch.current = true
      fetchCompanies()
    }
  }, [config.key, fetchCompanies])

  useRefetchOnFocus(fetchCompanies)

  const handleOpenCreate = () => {
    setEditingId(null)
    form.resetFields()
    form.setFieldsValue({ document_type: 'NIT' })
    setModalVisible(true)
  }

  const handleOpenEdit = (record: any) => {
    setEditingId(record.id)
    form.setFieldsValue({
      name: record.name,
      document_type: record.document_type,
      document_number: record.document_number,
      uid: record.uid
    })
    setModalVisible(true)
  }

  const handleSubmit = async (values: any) => {
    setSubmitLoading(true)
    try {
      let res;
      if (editingId) {
        res = await securePost('/company/update', {
          session: config.session,
          uidCompany: editingId,
          company: values
        })
        if (res?.message) throw new Error(res.message)
        message.success('Empresa actualizada exitosamente')
      } else {
        res = await securePost('/company/create', {
          session: config.session,
          company: values
        })
        if (res?.message) throw new Error(res.message)
        message.success('Empresa creada exitosamente')
      }
      setModalVisible(false)
      fetchCompanies()
    } catch (err: any) {
      console.error(err)
      message.error(err.message || 'Error al guardar empresa')
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '¿Está seguro de eliminar esta empresa?',
      content: 'Esta acción inhabilitará a la empresa en el sistema.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await securePost('/company/toggle-status', {
            session: config.session,
            uidCompany: id,
            is_active: false
          })
          message.success('Empresa eliminada exitosamente')
          fetchCompanies()
        } catch (err) {
          console.error(err)
          message.error('Error al eliminar empresa')
        }
      }
    })
  }

  const handleOpenModules = async (record: any) => {
    setSelectedCompanyId(record.id)
    setTransferLoading(true)
    setModulesModalVisible(true)
    try {
      const allRes = await securePost('/module/list', { session: config.session })
      const allMods = allRes?.modules || []
      
      const compRes = await securePost('/module/get-company-modules', { session: config.session, company_id: record.id })
      const compMods = compRes?.modules || []

      const formattedAll = allMods.map((m: any) => ({
        key: m.id.toString(),
        title: m.name,
        description: m.description,
        disabled: false
      }))
      setAllModules(formattedAll)

      const assignedKeys = compMods.map((m: any) => m.id.toString())
      setTargetKeys(assignedKeys)
    } catch (err) {
      console.error(err)
      message.error('Error al cargar módulos')
    } finally {
      setTransferLoading(false)
    }
  }

  const handleTransferChange = async (nextTargetKeys: React.Key[], direction: string, moveKeys: React.Key[]) => {
    if (!selectedCompanyId) return
    setTransferLoading(true)
    try {
      if (direction === 'right') {
        for (const key of moveKeys) {
          await securePost('/module/assign-to-company', {
            session: config.session,
            company_id: selectedCompanyId,
            module_id: Number.parseInt(key.toString())
          })
        }
      } else {
        for (const key of moveKeys) {
          await securePost('/module/remove-from-company', {
            session: config.session,
            company_id: selectedCompanyId,
            module_id: Number.parseInt(key.toString())
          })
        }
      }
      setTargetKeys(nextTargetKeys)
      message.success('Módulos actualizados exitosamente')
    } catch (err) {
      console.error(err)
      message.error('Error al actualizar módulos')
    } finally {
      setTransferLoading(false)
    }
  }

  const handleOpenSubscriptions = async (record: any) => {
    setSelectedCompany(record)
    setSubModalVisible(true)
    setSubLoading(true)
    subForm.resetFields()
    subForm.setFieldsValue({ auto_renew: true, start_date: dayjs() })
    try {
      const [plansRes, subsRes] = await Promise.all([
        securePost('/plan/list', { session: config.session }),
        securePost('/subscription/get-company-subscriptions', { session: config.session, company_id: record.id })
      ])

      if (plansRes && Array.isArray(plansRes.plans)) {
        setAvailablePlans(plansRes.plans)
      } else {
        setAvailablePlans([])
      }

      if (subsRes && Array.isArray(subsRes.subscriptions)) {
        setCompanySubs(subsRes.subscriptions)
      } else {
        setCompanySubs([])
      }
    } catch (err) {
      console.error(err)
      message.error('Error al cargar datos de suscripción')
    } finally {
      setSubLoading(false)
    }
  }

  const reloadCompanySubs = async (companyId: number) => {
    const subsRes = await securePost('/subscription/get-company-subscriptions', { session: config.session, company_id: companyId })
    if (subsRes && Array.isArray(subsRes.subscriptions)) {
      setCompanySubs(subsRes.subscriptions)
    }
  }

  const handlePlanSelectChange = (planId: number) => {
    const selectedPlan = availablePlans.find(p => p.id === planId)
    const durationDays = selectedPlan?.duration_days || 30
    
    const currentStart = subForm.getFieldValue('start_date') || dayjs()
    const computedEnd = dayjs(currentStart).add(durationDays, 'day')
    
    subForm.setFieldsValue({
      start_date: currentStart,
      end_date: computedEnd
    })
  }

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return
    const planId = subForm.getFieldValue('plan_id')
    const selectedPlan = availablePlans.find(p => p.id === planId)
    const durationDays = selectedPlan?.duration_days || 30
    
    const computedEnd = dayjs(date).add(durationDays, 'day')
    subForm.setFieldsValue({
      end_date: computedEnd
    })
  }

  const handleAssignPlan = async (values: any) => {
    if (!selectedCompany) return
    setSubFormLoading(true)
    try {
      const startDate = values.start_date ? values.start_date.toISOString() : new Date().toISOString()
      const endDate = values.end_date ? values.end_date.toISOString() : new Date().toISOString()

      const res = await securePost('/subscription/create', {
        session: config.session,
        subscription: {
          company_id: selectedCompany.id,
          plan_id: values.plan_id,
          start_date: startDate,
          end_date: endDate,
          auto_renew: values.auto_renew ?? true,
          status: 'ACTIVE'
        }
      })

      if (res && !res.error && !res.message?.includes('failed')) {
        message.success('Plan asignado exitosamente. Módulos del plan aprovisionados automáticamente.')
        subForm.resetFields()
        await reloadCompanySubs(selectedCompany.id)
      } else {
        message.error(res?.error || res?.message || 'Error al asignar plan a la empresa')
      }
    } catch (err: any) {
      console.error(err)
      message.error(err.message || 'Error al asignar plan a la empresa')
    } finally {
      setSubFormLoading(false)
    }
  }

  const handleToggleSubscriptionStatus = async (sub: any, newStatus: 'ACTIVE' | 'INACTIVE') => {
    try {
      const res = await securePost('/subscription/update', {
        session: config.session,
        idSubscription: sub.id,
        subscription: {
          status: newStatus
        }
      })

      if (res && !res.error && !res.message?.includes('failed')) {
        message.success(`Suscripción ${newStatus === 'ACTIVE' ? 'reactivada' : 'desactivada'} exitosamente`)
        if (selectedCompany) {
          await reloadCompanySubs(selectedCompany.id)
        }
      } else {
        message.error(res?.error || res?.message || 'Error al cambiar estado de la suscripción')
      }
    } catch (err) {
      console.error(err)
      message.error('Error al actualizar estado de la suscripción')
    }
  }

  const handleOpenEditSub = (sub: any) => {
    setEditingSub(sub)
    editSubForm.setFieldsValue({
      start_date: sub.start_date ? dayjs(sub.start_date) : dayjs(),
      end_date: sub.end_date ? dayjs(sub.end_date) : dayjs(),
      status: sub.status || 'ACTIVE',
      auto_renew: sub.auto_renew ?? true
    })
    setEditSubModalVisible(true)
  }

  const handleSaveEditSub = async (values: any) => {
    if (!editingSub || !selectedCompany) return
    setEditSubLoading(true)
    try {
      const res = await securePost('/subscription/update', {
        session: config.session,
        idSubscription: editingSub.id,
        subscription: {
          start_date: values.start_date ? values.start_date.toISOString() : editingSub.start_date,
          end_date: values.end_date ? values.end_date.toISOString() : editingSub.end_date,
          status: values.status,
          auto_renew: values.auto_renew
        }
      })

      if (res && !res.error && !res.message?.includes('failed')) {
        message.success('Suscripción actualizada exitosamente')
        setEditSubModalVisible(false)
        setEditingSub(null)
        await reloadCompanySubs(selectedCompany.id)
      } else {
        message.error(res?.error || res?.message || 'Error al actualizar suscripción')
      }
    } catch (err: any) {
      console.error(err)
      message.error(err.message || 'Error al actualizar suscripción')
    } finally {
      setEditSubLoading(false)
    }
  }

  const handleOpenElectronicInvoicing = (record: any) => {
    setSelectedFeCompany(record)
    setFeModalVisible(true)
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Nombre', dataIndex: 'name', key: 'name' },
    { title: 'Tipo Doc.', dataIndex: 'document_type', key: 'document_type' },
    { title: 'Número Doc.', dataIndex: 'document_number', key: 'document_number' },
    { title: 'UID', dataIndex: 'uid', key: 'uid' },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space wrap size="small">
          <Button icon={<EditOutlined />} onClick={() => handleOpenEdit(record)} size="small">Editar</Button>
          <Button icon={<SafetyCertificateOutlined />} onClick={() => handleOpenElectronicInvoicing(record)} size="small">Fact. Electrónica</Button>
          <Button icon={<IdcardOutlined />} onClick={() => handleOpenSubscriptions(record)} size="small">Suscripción / Plan</Button>
          <Button icon={<AppstoreOutlined />} onClick={() => handleOpenModules(record)} size="small">Módulos</Button>
          <Button type="primary" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} size="small">Eliminar</Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Gestión de Empresas</h3>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Crear Empresa
        </Button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
          <Spin />
        </div>
      ) : (
        <Table dataSource={companies} columns={columns} rowKey="id" scroll={{ x: 'max-content' }} />
      )}

      <Modal
        title={editingId ? 'Editar Empresa' : 'Crear Empresa'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={submitLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese nombre' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="document_type" label="Tipo de Documento" rules={[{ required: true, message: 'Seleccione tipo' }]}>
            <Select>
              <Select.Option value="NIT">NIT</Select.Option>
              <Select.Option value="RUT">RUT</Select.Option>
              <Select.Option value="Cedula">Cédula</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="document_number" label="Número de Documento" rules={[{ required: true, message: 'Ingrese número' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="uid" label="UID Único" rules={[
            { required: false },
            { pattern: /^[A-Z0-9\-_]+$/, message: 'Solo mayúsculas, números y sin espacios (se permite - y _)' }
          ]}>
            <Input placeholder="Ej. RYO-SAS (Opcional)" disabled={!!editingId} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Gestionar Módulos de la Empresa"
        open={modulesModalVisible}
        onCancel={() => setModulesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModulesModalVisible(false)}>Cerrar</Button>
        ]}
        width={600}
      >
        <Spin spinning={transferLoading}>
          <Transfer
            dataSource={allModules}
            titles={['Disponibles', 'Asignados']}
            targetKeys={targetKeys}
            onChange={handleTransferChange}
            render={item => item.title as string}
            listStyle={{ width: 250, height: 300 }}
          />
        </Spin>
      </Modal>

      {/* Modal de Planes y Suscripciones de Empresa */}
      <Modal
        title={`Suscripción y Plan: ${selectedCompany?.name || ''}`}
        open={subModalVisible}
        onCancel={() => setSubModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSubModalVisible(false)}>Cerrar</Button>
        ]}
        width={750}
      >
        <Spin spinning={subLoading}>
          <Card title="Asignar / Renovar Plan de Empresa" size="small" style={{ marginBottom: 20 }}>
            <Form form={subForm} layout="vertical" onFinish={handleAssignPlan} initialValues={{ auto_renew: true, start_date: dayjs() }}>
              <Form.Item name="plan_id" label="Seleccionar Plan Comercial" rules={[{ required: true, message: 'Seleccione un plan' }]}>
                <Select placeholder="Seleccione el plan a asignar" onChange={handlePlanSelectChange}>
                  {availablePlans.map((p: any) => {
                    const isAlreadyActive = companySubs.some((sub: any) => sub.plan_id === p.id && sub.status === 'ACTIVE')
                    return (
                      <Select.Option key={p.id} value={p.id} disabled={isAlreadyActive}>
                        {p.name} - ${p.price?.toLocaleString()} ({p.duration_days || 30} días){isAlreadyActive ? ' — (Ya Activo)' : ''}
                      </Select.Option>
                    )
                  })}
                </Select>
              </Form.Item>

              <Space style={{ display: 'flex', width: '100%' }} size="middle">
                <Form.Item 
                  name="start_date" 
                  label="Fecha de Inicio" 
                  rules={[{ required: true, message: 'Seleccione fecha inicio' }]}
                  style={{ flex: 1 }}
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" onChange={handleStartDateChange} />
                </Form.Item>
                <Form.Item 
                  name="end_date" 
                  label="Fecha de Vencimiento (Calculada)" 
                  rules={[{ required: true, message: 'Seleccione fecha vencimiento' }]}
                  style={{ flex: 1 }}
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled />
                </Form.Item>
              </Space>

              <Form.Item name="auto_renew" label="Renovación Automática" valuePropName="checked">
                <Switch />
              </Form.Item>
              
              <Button type="primary" htmlType="submit" loading={subFormLoading} icon={<CheckCircleOutlined />}>
                Asignar / Renovar Plan
              </Button>
            </Form>
          </Card>

          <Card title="Historial de Suscripciones" size="small">
            <List
              dataSource={companySubs}
              locale={{ emptyText: 'No hay suscripciones registradas' }}
              renderItem={(sub: any) => (
                <List.Item
                  actions={[
                    sub.status === 'ACTIVE' ? (
                      <Popconfirm
                        key="deactivate"
                        title="¿Desactivar suscripción?"
                        description="La empresa perderá la vigencia activa de este plan."
                        onConfirm={() => handleToggleSubscriptionStatus(sub, 'INACTIVE')}
                        okText="Sí, desactivar"
                        cancelText="No"
                      >
                        <Button danger size="small" icon={<StopOutlined />}>
                          Desactivar
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Popconfirm
                        key="activate"
                        title="¿Reactivar suscripción?"
                        description="Esta suscripción pasará a estar ACTIVE y se aprovisionarán los módulos del plan."
                        onConfirm={() => handleToggleSubscriptionStatus(sub, 'ACTIVE')}
                        okText="Sí, reactivar"
                        cancelText="No"
                      >
                        <Button type="primary" size="small" icon={<PlayCircleOutlined />}>
                          Reactivar
                        </Button>
                      </Popconfirm>
                    ),
                    <Button key="edit" size="small" icon={<EditOutlined />} onClick={() => handleOpenEditSub(sub)}>
                      Editar Fechas
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span style={{ fontWeight: 600 }}>{sub.plans?.name || `Plan #${sub.plan_id}`}</span>
                        <Tag color={sub.status === 'ACTIVE' ? 'green' : sub.status === 'INACTIVE' ? 'orange' : 'default'}>
                          {sub.status}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div>
                          <CalendarOutlined style={{ marginRight: 6 }} />
                          Inicio: {dayjs(sub.start_date).format('YYYY-MM-DD')} | Vencimiento: {dayjs(sub.end_date).format('YYYY-MM-DD')}
                        </div>
                        <div>Renovación Automática: {sub.auto_renew ? 'Sí' : 'No'}</div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Spin>
      </Modal>

      {/* Modal para Editar Fechas y Estado de Suscripción Existente */}
      <Modal
        title={`Editar Suscripción #${editingSub?.id || ''}`}
        open={editSubModalVisible}
        onCancel={() => {
          setEditSubModalVisible(false)
          setEditingSub(null)
        }}
        onOk={() => editSubForm.submit()}
        confirmLoading={editSubLoading}
        okText="Guardar Cambios"
        cancelText="Cancelar"
      >
        <Form form={editSubForm} layout="vertical" onFinish={handleSaveEditSub}>
          <Form.Item name="start_date" label="Fecha de Inicio" rules={[{ required: true, message: 'Seleccione fecha de inicio' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="end_date" label="Fecha de Vencimiento" rules={[{ required: true, message: 'Seleccione fecha de vencimiento' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="status" label="Estado de Suscripción" rules={[{ required: true, message: 'Seleccione estado' }]}>
            <Select>
              <Select.Option value="ACTIVE">ACTIVE (Activa)</Select.Option>
              <Select.Option value="INACTIVE">INACTIVE (Inactiva)</Select.Option>
              <Select.Option value="EXPIRED">EXPIRED (Vencida)</Select.Option>
              <Select.Option value="CANCELLED">CANCELLED (Cancelada)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="auto_renew" label="Renovación Automática" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Configuración y Rangos de Facturación Electrónica DIAN */}
      <CompanyElectronicInvoicingModal
        visible={feModalVisible}
        company={selectedFeCompany}
        onClose={() => {
          setFeModalVisible(false)
          setSelectedFeCompany(null)
        }}
      />
    </div>
  )
}

export default CompaniesView
